/**
 * Job Handlers — JOB_READY / JOB_ERROR socket events.
 *
 * When the server's fal webhook fires and a queued job becomes ready (or
 * fails), the server emits JOB_READY / JOB_ERROR to the user's socket room.
 * We store the update keyed by request_id so ToolUseBlock can swap a queued
 * pill into the rendered media (or a clean error variant) in-place without
 * any prop-drilling.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { useJobStore } from "../jobStore";

interface JobReadyPayload {
  jobId: string;
  task_id?: string | null;
  request_id: string;
  messageId?: string;
  blockId?: string;
  result: Record<string, unknown>;
}

interface JobErrorPayload {
  jobId: string;
  task_id?: string | null;
  request_id: string;
  messageId?: string;
  blockId?: string;
  /** Whether the failed job was refunded. Absent → the canvas treats it as
   *  refunded; explicit false is the unresolvable refund-0 edge. */
  refunded?: boolean;
  /** The submit's original brief, for prefilling Retry on a fully-failed round. */
  brief?: string | null;
  error: string;
}

interface JobProgressPayload {
  jobId: string;
  task_id?: string | null;
  request_id: string;
  blockId?: string;
  phase: "generating";
}

export function registerJobHandlers(socket: Socket): () => void {
  const handleJobReady = (payload: JobReadyPayload) => {
    if (!payload?.request_id) return;
    useJobStore.getState().setJobUpdate({
      jobId: payload.jobId,
      request_id: payload.request_id,
      task_id: payload.task_id,
      blockId: payload.blockId,
      result: payload.result,
    });
  };

  const handleJobError = (payload: JobErrorPayload) => {
    if (!payload?.request_id) return;
    useJobStore.getState().setJobUpdate({
      jobId: payload.jobId,
      request_id: payload.request_id,
      task_id: payload.task_id,
      blockId: payload.blockId,
      refunded: payload.refunded,
      brief: payload.brief,
      error: payload.error,
    });
  };

  const handleJobProgress = (payload: JobProgressPayload) => {
    if (!payload?.request_id) return;
    useJobStore.getState().setJobUpdate({
      jobId: payload.jobId,
      request_id: payload.request_id,
      task_id: payload.task_id,
      blockId: payload.blockId,
      progressPhase: payload.phase,
    });
  };

  socket.on(EVENTS.JOB_READY, handleJobReady);
  socket.on(EVENTS.JOB_ERROR, handleJobError);
  socket.on(EVENTS.JOB_PROGRESS, handleJobProgress);

  return () => {
    socket.off(EVENTS.JOB_READY, handleJobReady);
    socket.off(EVENTS.JOB_ERROR, handleJobError);
    socket.off(EVENTS.JOB_PROGRESS, handleJobProgress);
  };
}
